import { useQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { BookText, ClipboardList, FlaskConical } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { orpc } from "@/client";
import { ErrorBoundary } from "@/components/error-boundary";
import { ExamsTab } from "@/components/exams/exams-tab";
import { ManualsList } from "@/components/manuals/manuals-list";
import { ProntuarioTab } from "@/components/medico/prontuario-tab";
import { EmptyState } from "@/components/ui/empty-state";
import { PessoaLabels } from "@/lib/labels";

export type ClinicalKind = "prontuario" | "exames" | "manual";

interface ClinicalAnchor {
	patientId: number;
}

const KIND_ICON: Record<ClinicalKind, LucideIcon> = {
	prontuario: ClipboardList,
	exames: FlaskConical,
	manual: BookText,
};

function ClinicalEmpty({ kind }: { kind: ClinicalKind }) {
	return <EmptyState icon={KIND_ICON[kind]} title={PessoaLabels.fichaTabPlaceholder[kind]} />;
}

function AnchoredPanel({
	kind,
	anchor,
}: {
	kind: "prontuario" | "exames";
	anchor: ClinicalAnchor;
}) {
	const patient = useQuery(
		orpc.medico.patientByPerson.queryOptions({ input: anchor, retry: false }),
	);

	const medPatientId = patient.data?.medPatientId;

	if (medPatientId === undefined || medPatientId === null) {
		return <ClinicalEmpty kind={kind} />;
	}

	if (kind === "prontuario") {
		return (
			<ErrorBoundary>
				<Suspense fallback={null}>
					<ProntuarioTab medPatientId={medPatientId} />
				</Suspense>
			</ErrorBoundary>
		);
	}

	return (
		<ErrorBoundary>
			<Suspense fallback={null}>
				<ExamsTab patientId={medPatientId} />
			</Suspense>
		</ErrorBoundary>
	);
}

function ManualPanel({ anchor }: { anchor: ClinicalAnchor }) {
	const summary = useQuery(orpc.manuals.byPerson.queryOptions({ input: anchor, retry: false }));

	if (summary.data === undefined || summary.data === null) {
		return <ClinicalEmpty kind="manual" />;
	}

	return <ManualsList summary={summary.data} />;
}

export function ClinicalPanel({ kind, anchor }: { kind: ClinicalKind; anchor: ClinicalAnchor }) {
	if (kind === "manual") {
		return <ManualPanel anchor={anchor} />;
	}

	return <AnchoredPanel kind={kind} anchor={anchor} />;
}
